import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cancelReservation, getReservations } from '@/lib/booking/api';
import type { Reservation, ReservationRef } from '@/lib/booking/types';

// HOLD → Confirm (book) lives in use-holds.ts (UI-own HOLD state). This module
// keeps the reservations-by-case query + the cancel saga on durable rows.

const KEY = (caseUrn: string) => ['cases', caseUrn, 'reservations'] as const;

/**
 * Reservations-by-case — WS-driven, not polled. The async book/cancel acks 202
 * and a worker in apps/booking flips the row to its terminal status
 * (BOOKED/CANCELLED/FAILED/CANCEL_FAILED). That settlement now reaches the
 * operator-bff over Kafka ({tenant}.booking.{success,failed,cancelled}), which
 * fans a `CaseChanged` (kind 'booking') to the case room; the central applyEvent
 * merge invalidates this exact key (['cases', caseUrn, 'reservations']) so the
 * badge advances on its own — no `refetchInterval`, per the app-wide WS-only rule.
 */
export function useReservations(caseUrn: string) {
  return useQuery<Reservation[]>({
    queryKey: KEY(caseUrn),
    queryFn: () => getReservations(caseUrn),
    enabled: !!caseUrn,
    // Poll every 1s when any reservation is in PENDING status so it settles to BOOKED/CANCELLED.
    refetchInterval: (query) => {
      const rows = query.state.data;
      if (Array.isArray(rows) && rows.some((r) => r.status === 'PENDING')) {
        return 1000;
      }
      return false;
    },
    // Always refetch on mount/focus so a row that settled while the tab was
    // backgrounded (or before the socket connected) shows its terminal status.
    refetchOnWindowFocus: true,
  });
}

export interface CancelInput {
  reservationUrn: string;
  reasonCode: string;
  idempotencyKey: string;
}

export function useCancelReservation(caseUrn: string) {
  const qc = useQueryClient();
  return useMutation<ReservationRef, Error, CancelInput>({
    mutationFn: ({ reservationUrn, reasonCode, idempotencyKey }) =>
      // 4th arg subCaseUrn (lock key) is omitted here — this board-level cancel
      // doesn't track the PNR's sub-case. Pass caseUrn (5th) so the BFF fans out
      // a CaseChanged WS event to the case room (other operators' reservations
      // refetch).
      cancelReservation(reservationUrn, reasonCode, idempotencyKey, undefined, caseUrn),
    // Optimistically flip the row to PENDING so the badge shows in-flight while
    // the cancel saga runs (the worker settles it to CANCELLED / CANCEL_FAILED,
    // and the WS CaseChanged then invalidates this slice).
    onMutate: ({ reservationUrn }) => {
      qc.setQueryData<Reservation[]>(KEY(caseUrn), (prev) =>
        Array.isArray(prev)
          ? prev.map((r) => (r.reservationUrn === reservationUrn ? { ...r, status: 'PENDING' } : r))
          : prev,
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY(caseUrn) }),
  });
}
